<template>
  <v-container class="wrapper">
    <div>
      <p class="sub-title">{{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.SUB_TITLE') }}</p>
      <v-radio-group class="repairer-offer" v-model="selectedOffer">
        <div
          :key="'repairerOffer-' + index"
          v-for="(repairerOffer, index) of currentRepairFile?.repairer_estimate
            ?.repairer_estimate_option_list"
          :id="'repairerOffer-' + index"
          class="repairer-offer-content"
        >
          <v-col cols="2" class="py-0">
            <v-radio :value="repairerOffer" color="#559A1B" />
          </v-col>
          <v-col cols="10" class="px-0">
            <div class="d-flex align-center">
              <p class="price-text">
                {{ $t('COMMON.PRODUCT_END_STATUS.' + repairerOffer.offer_type.toUpperCase()) }}
              </p>
              <v-spacer></v-spacer>
              <p
                class="price-text"
                v-if="!visiblePriceInputField.includes(repairerOffer.technical_id)"
              >
                {{ Number(repairerOffer.total_cost).toFixed(2) + $t('COMMON.EURO') }}
              </p>
            </div>
            <div v-if="selectedOffer?.technical_id === repairerOffer?.technical_id">
              <div class="d-flex align-center justify-space-between w-100 pt-3">
                <p class="offer-title">
                  {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.TRANSPORT_COST') }}
                </p>
                <p class="offer-title">
                  {{
                    (
                      repairerOffer.estimate_cost +
                      repairerOffer.transport_cost +
                      repairerOffer.labor_cost
                    ).toFixed(2)
                  }}
                  {{ $t('COMMON.EURO') }}
                </p>
              </div>
              <div class="line-content" />
              <div class="d-flex align-center justify-space-between w-100">
                <p class="offer-text">{{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MARGIN') }}</p>
                <p
                  class="offer-text"
                  v-if="!visibleMarginInputField.includes(repairerOffer.technical_id)"
                >
                  {{ repairerOffer.margin }} {{ $t('COMMON.PERCENT') }}
                </p>
              </div>
              <div class="d-flex align-center justify-start ga-2 w-100">
                <!--- PRICE UPDATE --->
                <v-btn
                  class="boom-button add-boom-button"
                  variant="outlined"
                  v-if="
                    !visiblePriceInputField.includes(repairerOffer.technical_id) &&
                    !visibleMarginInputField.includes(repairerOffer.technical_id)
                  "
                  @click="togglePriceTextField(repairerOffer.technical_id)"
                >
                  {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MODIFICATION_PRICE') }}
                </v-btn>
                <v-form
                  ref="formUpdateEstimateRepairerOption"
                  v-model="formUpdateEstimateRepairerOptionIsValid"
                  @submit.prevent="updateRepairerEstimateOptionFromPrice"
                  class="d-flex align-center justify-space-between w-100 mt-6"
                  v-if="visiblePriceInputField.includes(repairerOffer.technical_id)"
                >
                  <div class="margin-input">
                    <v-text-field
                      reverse
                      v-model.number="repairerOffer.total_cost"
                      type="number"
                      :rules="priceRules"
                      @change="repairerOffer.total_cost = parseFloat(repairerOffer.total_cost)"
                      hide-details
                    >
                      <template v-slot:prepend-inner>
                        <v-btn
                          class="boom-button pl-5 pr-5 h-100"
                          :disabled="!formUpdateEstimateRepairerOptionIsValid || loading"
                          @click="updateRepairerEstimateOptionFromPrice(repairerOffer)"
                        >
                          {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.SAVE_PRICE') }}
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                </v-form>

                <!--- MARGIN UPDATE --->
                <v-btn
                  class="boom-button add-boom-button"
                  variant="outlined"
                  v-if="
                    !visibleMarginInputField.includes(repairerOffer.technical_id) &&
                    !visiblePriceInputField.includes(repairerOffer.technical_id)
                  "
                  @click="toggleMarginTextField(repairerOffer.technical_id)"
                >
                  {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MODIFICATION_MARGIN') }}
                </v-btn>
                <v-form
                  ref="formUpdateEstimateRepairerOption"
                  v-model="formUpdateEstimateRepairerOptionIsValid"
                  @submit.prevent="updateRepairerEstimateOptionFromMargin"
                  class="d-flex align-center justify-space-between w-100 mt-6"
                  v-if="visibleMarginInputField.includes(repairerOffer.technical_id)"
                >
                  <div class="margin-input">
                    <v-text-field
                      reverse
                      v-model.number="repairerOffer.margin"
                      type="number"
                      :rules="marginRules"
                      hide-details
                    >
                      <template v-slot:prepend-inner>
                        <v-btn
                          class="boom-button pl-5 pr-5 h-100"
                          :disabled="!formUpdateEstimateRepairerOptionIsValid || loading"
                          @click="updateRepairerEstimateOptionFromMargin(repairerOffer)"
                        >
                          {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.SAVE_MARGIN') }}
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                </v-form>
              </div>
            </div>
          </v-col>
        </div>
      </v-radio-group>
    </div>

    <v-row>
      <!-- EMAIL -->
      <v-form v-model="isEmailFormValid" class="w-100">
        <v-col cols="12">
          <p class="sub-title">{{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.SEND_ESTIMATE_AGAIN') }}</p>
          <v-text-field
            required
            v-model="email"
            :rules="[...requiredRules, ...emailRules]"
            append-inner-icon="mdi-pencil-outline"
            hide-details="auto"
          >
            <template v-slot:prepend-inner>
              <img src="@/assets/icons/file/mail.svg" alt="mail-icon" class="mail-icon" />
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12">
          <v-btn
            class="boom-button email-button w-100"
            :disabled="loading || !isEmailFormValid"
            :loading="loading"
            variant="outlined"
            @click="sendClientEmail"
          >
            <p>{{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.BUTTON.SEND_EMAIL') }}</p>
          </v-btn>
        </v-col>
      </v-form>
    </v-row>

    <div class="bottom-content">
      <v-col cols="12" class="px-0">
        <v-btn
          class="boom-button w-100"
          :disabled="loading || !selectedOffer"
          :loading="loading"
          variant="outlined"
          @click="submit"
        >
          {{ $t('COMMON.BUTTON.CONTINUE') }}
        </v-btn>
      </v-col>
      <v-btn
        class="button w-100"
        :loading="loading"
        variant="outlined"
        @click="$emit('cancelEstimateRepairer')"
      >
        {{ $t('COMMON.BUTTON.CANCEL') }}
      </v-btn>
    </div>
  </v-container>
</template>

<script>
import Validator from '@/components/utils/validator'
import RepairFileService from '@/services/RepairFileService'
import { RepairFile } from '@/models/RepairFile'
import { displayToast, handleResponseError } from '@/components/utils/globalFunction'
import { auth as $store } from '@/store/AuthModule'

export default {
  props: {
    currentRepairFile: RepairFile
  },
  data() {
    return {
      loading: false,
      error: null,
      formUpdateEstimateRepairerOptionIsValid: false,
      repairer_estimate_option_list: null,
      selectedOffer: null,
      visiblePriceInputField: [],
      visibleMarginInputField: [],

      priceRules: Validator.numberRules,
      marginRules: Validator.marginRules,
      requiredRules: Validator.requiredRules,
      emailRules: Validator.emailRules,

      email: null,
      isEmailFormValid: false
    }
  },
  created() {
    this.patchData()
  },
  methods: {
    async submit() {
      this.selectedOffer.is_selected = true
      this.$emit('openEstimateRepairerClientChoiceValidation')
    },
    toggleMarginTextField(id) {
      if (this.visibleMarginInputField.includes(id)) {
        this.visibleMarginInputField = this.visibleMarginInputField.filter(
          (visibleId) => visibleId !== id
        )
      } else {
        this.visibleMarginInputField.push(id)
      }
    },
    togglePriceTextField(id) {
      if (this.visiblePriceInputField.includes(id)) {
        this.visiblePriceInputField = this.visiblePriceInputField.filter(
          (visibleId) => visibleId !== id
        )
      } else {
        this.visiblePriceInputField.push(id)
      }
    },
    updateRepairerEstimateOptionFromMargin(repairerEstimateOption) {
      repairerEstimateOption.total_cost =
        (repairerEstimateOption.estimate_cost +
          repairerEstimateOption.transport_cost +
          repairerEstimateOption.labor_cost) /
        (1 - repairerEstimateOption.margin / 100)
      this.toggleMarginTextField(repairerEstimateOption.technical_id)
    },
    updateRepairerEstimateOptionFromPrice(repairerEstimateOption) {
      const newMargin =
        (100 *
          (repairerEstimateOption.total_cost -
            (repairerEstimateOption.estimate_cost +
              repairerEstimateOption.transport_cost +
              repairerEstimateOption.labor_cost))) /
        repairerEstimateOption.total_cost
      repairerEstimateOption.margin = newMargin.toFixed(0)
      this.togglePriceTextField(repairerEstimateOption.technical_id)
    },
    patchData() {
      this.email = this.currentRepairFile?.email
    },
    getCurrentCollaborator() {
      return $store.state.currentCollaborator
    },
    async sendClientEmail() {
      this.loading = true
      try {
        const data = {
          requestor_collaborator_id: this.getCurrentCollaborator().technical_id,
          repair_file_id: this.currentRepairFile.technical_id,
          contact_email: this.email
        }
        await RepairFileService.sendNewRepairFileRepairerEstimateToClient(data)
        displayToast(this.$t('ESTIMATE_REPAIRER_DETAIL.MODAL.SEND_CLIENT_EMAIL'), 'success')
        this.$emit('updateCurrentRepairFile')
        this.$emit('closeNewAction')
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.sub-title {
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 20px;
}

.button {
  display: flex;
  padding: 10px 20px;
  justify-content: center;
  align-items: center;
  text-transform: none !important;
  border-radius: 20px;
  border: 1px solid #000;
  background: #fff;
  color: #000;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

:deep(.v-infinite-scroll__side) {
  padding: unset;
}

.wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.bottom-content {
  margin-top: auto;
}

.repairer-offer {
  margin-bottom: 40px;
}

.repairer-offer-content {
  display: flex;
  align-items: center;

  padding: 10px 20px;
  border-radius: 20px;
  border: 1px solid #559a1b;
  background: #fafafa;
  margin-bottom: 20px;
}

.price-text {
  font-size: 18px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
}

:deep(.v-selection-control) {
  max-width: 40px !important;
}

.line-content {
  width: 100%;
  margin: 5px 0 5px 0;
  border-top: 1px solid black;
}

.add-boom-button {
  margin-top: 10px;
}

.boom-button {
  padding: 10px 20px !important;
}

.margin-input {
  width: 100%;
}

.save-boom-button {
  border-radius: 30px 30px 30px 30px !important;
  padding: 28px 20px !important;
}

:deep(.v-field--prepended) {
  padding-left: 0 !important;
}

.offer-title {
  font-size: 16px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
}

.offer-text {
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
}

.mail-icon {
  width: 20px;
  height: 20px;
  margin-left: 10px;
  margin-right: 10px;
  filter: invert(0%) sepia(100%) saturate(7461%) hue-rotate(61deg) brightness(106%) contrast(109%);
}

.boom-button {
  padding: 10px 20px !important;
}

.email-button {
  margin-bottom: 20px;
}
</style>
